import { Rocket } from "lucide-react";
import type { PlatformProject } from "@/lib/platformTypes";
import { EmptyState, ViewSection, relativeTime } from "@/views/shared";
import { HomeRow, HomeRowList, HomeRowsSkeleton, ViewAllAction } from "./HomeRow";

export function projectLiveUrl(p: PlatformProject): string | null {
  return p.productionUrl ?? p.url ?? null;
}

export function projectDeployedAt(p: PlatformProject): string | null {
  return p.lastDeployAt ?? p.lastDeployedAt ?? null;
}

export function RecentlyShipped({
  projects,
  loaded,
  error,
  onOpen,
  onViewAll,
}: {
  projects: PlatformProject[];
  loaded: boolean;
  error?: string;
  onOpen: (id: string) => void;
  onViewAll: () => void;
}) {
  return (
    <ViewSection title="Recently shipped" action={<ViewAllAction onClick={onViewAll} />}>
      {!loaded ? (
        <HomeRowsSkeleton rows={3} />
      ) : error ? (
        <EmptyState message={error} />
      ) : projects.length === 0 ? (
        <EmptyState message="Nothing deployed yet." />
      ) : (
        <HomeRowList>
          {projects.map((p) => {
            const url = projectLiveUrl(p);
            return (
              <HomeRow key={p.id} onClick={() => onOpen(p.id)}>
                <Rocket className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-foreground">{p.name}</div>
                  <div className="flex items-center gap-1.5 truncate text-[10px] text-muted-foreground">
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        // The row itself navigates to the project detail; the
                        // URL is a separate affordance that opens the live site.
                        onClick={(e) => e.stopPropagation()}
                        className="truncate font-mono text-primary/80 hover:text-primary hover:underline"
                      >
                        {url.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                    <span className="shrink-0">{relativeTime(projectDeployedAt(p))}</span>
                  </div>
                </div>
              </HomeRow>
            );
          })}
        </HomeRowList>
      )}
    </ViewSection>
  );
}
